import { Layers } from "lucide-react";
import React from "react";
import { AnimatedGroup } from "@/components/ui/animated-group";

// Komponent podglądu
export const AnimatedGroupPreview = () => (
  <AnimatedGroup preset="" className="grid grid-cols-3 gap-3">
    {Array.from({ length: 6 }, (_, i) => (
      <div
        className="w-12 h-12 bg-primary/20 dark:bg-primary/40 rounded-md flex items-center justify-center"
        key={i}
      >
        {i + 1}
      </div>
    ))}
  </AnimatedGroup>
);

const component = {
  id: "animated-group",
  title: "Animated Group",
  component: AnimatedGroup,
  description:
    "A component that animates a group of elements with staggered entrances.",
  preview: AnimatedGroupPreview,
  icon: <Layers className="h-4 w-4 mr-2" />,
  code: `import { AnimatedGroup } from "@/components/ui/animated-group";

export const AnimatedGroupExample = () => {
  return (
    <AnimatedGroup className="grid grid-cols-3 gap-4">
      {Array.from({ length: 6 }, (_, i) => (
        <div className="h-12 w-12 bg-primary/20 rounded-md" key={i}>
          {i + 1}
        </div>
      ))}
    </AnimatedGroup>
  );
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/animated-group.jsx\`
   - Make sure you have \`framer-motion\` installed in your dependencies

### Dependencies

\`\`\`json
{
  "dependencies": {
    "framer-motion": "^10.12.16",
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}
\`\`\`
`,
  sourceCode: `// src/components/ui/animated-group.jsx
"use client";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";
import React, { useRef } from "react";

const defaultContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const defaultItemVariants = {
  hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.75,
      type: "spring",
      fade: 0.3,
      filter: { min: 0 },
    },
  },
};

const presetVariants = {
  fade: {
    container: defaultContainerVariants,
    item: { hidden: { opacity: 0 }, visible: { opacity: 1 } },
  },
  slide: {
    container: defaultContainerVariants,
    item: { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } },
  },
  scale: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { opacity: 1, scale: 1 },
    },
  },
  blur: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, filter: "blur(4px)" },
      visible: {
        opacity: 1,
        filter: "blur(0px)",
        transition: {
          duration: 0.5,
          filter: { min: 0 },
        },
      },
    },
  },
  "blur-slide": {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, filter: "blur(4px)", y: 20 },
      visible: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          duration: 0.5,
          filter: { min: 0 },
        },
      },
    },
  },
  zoom: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, scale: 0.5 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: { type: "spring", stiffness: 300, damping: 20 },
      },
    },
  },
  flip: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, rotateX: -90 },
      visible: {
        opacity: 1,
        rotateX: 0,
        transition: { type: "spring", stiffness: 300, damping: 20 },
      },
    },
  },
  bounce: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, y: -50 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 400, damping: 10 },
      },
    },
  },
  rotate: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, rotate: -180 },
      visible: {
        opacity: 1,
        rotate: 0,
        transition: { type: "spring", stiffness: 200, damping: 15 },
      },
    },
  },
  swing: {
    container: defaultContainerVariants,
    item: {
      hidden: { opacity: 0, rotate: -10 },
      visible: {
        opacity: 1,
        rotate: 0,
        transition: { type: "spring", stiffness: 300, damping: 8 },
      },
    },
  },
};

function AnimatedGroup({
  children,
  itemClassName,
  className,
  variants,
  preset,
}) {
  const selectedVariants = preset
    ? presetVariants[preset]
    : { container: defaultContainerVariants, item: defaultItemVariants };
  const containerVariants = variants?.container || selectedVariants.container;
  const itemVariants = variants?.item || selectedVariants.item;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : ""}
      variants={containerVariants}
      className={cn(className)}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div
          key={index}
          variants={itemVariants}
          className={cn("h-full w-full ", itemClassName)}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}

export { AnimatedGroup };
`,
  props: [
    {
      name: "delay",
      type: "number",
      default: "0.1",
      description: "Delay between each child's animation in seconds.",
    },
    {
      name: "preset",
      type: "string",
      default: "undefined",
      description: "The preset to use for the animation.",
    },
  ],
  examples: [
    {
      title: "With grid layout",
      code: `<AnimatedGroup className="grid grid-cols-3 gap-4">
  {items.map((item) => (
    <Card key={item.id}>{item.content}</Card>
  ))}
</AnimatedGroup>`,
    },
    {
      title: "With custom delay",
      code: `<AnimatedGroup delay={0.2} className="flex flex-col gap-2">
  {items.map((item) => (
    <div key={item.id}>{item.content}</div>
  ))}
</AnimatedGroup>`,
    },
  ],
};

export default component;
